import { LiveStreamActivePlaylist } from '@clipr/lib';
import { JobModel } from '../../entities';
import { durationToString } from '../../core/time';
import { PlayerLiveAdapter } from './playerLiveAdapter';

// ---------------------------------------------------------------------------
// Context interface — the subset of PlayerState needed by the live-stream
// helpers, avoiding a direct import of PlayerState.
// ---------------------------------------------------------------------------

export interface LiveContext {
  readonly isLiveStream: boolean;
  readonly isSeekDragging: boolean;
  readonly isSeekRequested: boolean;
  readonly currentSeekTime: number | null;
  readonly currentTime: number;
  readonly isMounted: boolean;
  readonly suppressLiveReactions: boolean;
  readonly playerLiveAdapter: PlayerLiveAdapter;
  readonly job: JobModel | null;
}

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

export function isLiveStreamRunning(ctx: LiveContext): boolean {
  return ctx.isLiveStream && !ctx.playerLiveAdapter.isHlsStreamCompleted;
}

export function isRealTime(ctx: LiveContext): boolean {
  return ctx.isLiveStream && ctx.playerLiveAdapter.isRealTime;
}

export function isSeekable(ctx: LiveContext): boolean {
  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.isSeekable;
  return true;
}

export function showLiveWaitingScreen(ctx: LiveContext): boolean {
  const { job } = ctx;
  if (!job) return false;

  return (job.isLiveStreamWaiting &&
    !(isLivePlaybackActive(ctx) || ctx.playerLiveAdapter.isPlaybackCompleted)) ?? false;
}

export function showLiveNotStreamingScreen(
  ctx: LiveContext,
  playlistType: LiveStreamActivePlaylist | null,
): boolean {
  const { job } = ctx;
  if (!job) return false;

  return (
    (playlistType === LiveStreamActivePlaylist.Primary && !job.isPlaylistActive(LiveStreamActivePlaylist.Primary)) ||
    (playlistType === LiveStreamActivePlaylist.Secondary && !job.isPlaylistActive(LiveStreamActivePlaylist.Secondary)) ||
    (!job.isPlaylistActive(LiveStreamActivePlaylist.Primary) && job.isPlaylistSelected(LiveStreamActivePlaylist.Primary)) ||
    (!job.isPlaylistActive(LiveStreamActivePlaylist.Secondary) && job.isPlaylistSelected(LiveStreamActivePlaylist.Secondary)) ||
    (job.isLive && !job.playlists?.primaryActive && !job.playlists?.secondaryActive)) && !job.isLiveProcessingDone;
}

export function showLiveEndedScreen(ctx: LiveContext): boolean {
  return ((ctx.job?.isLiveProcessing || ctx.job?.isLiveStreamEnded) && !isLivePlaybackActive(ctx)) ?? false;
}

export function getLiveOffsetLabel(ctx: LiveContext): string {
  const duration = ctx.playerLiveAdapter.duration;

  let time: number;
  if (ctx.isSeekDragging || ctx.isSeekRequested) {
    time = duration - (ctx.currentSeekTime ?? 0);
  } else {
    time = duration - ctx.currentTime;
  }

  return (Math.round(time) > 0 ? '-' : '') + durationToString(time, 'time');
}

export function showLiveReactions(ctx: LiveContext): boolean {
  return isRealTime(ctx) && !ctx.suppressLiveReactions;
}

export function isLivePlaybackActive(ctx: LiveContext): boolean {
  return !!ctx.job?.isLive && ctx.playerLiveAdapter.isPlaybackActive;
}

export function isMonitorEnabled(ctx: LiveContext): boolean {
  const { job, playerLiveAdapter } = ctx;
  if (!job) return false;

  return ctx.isMounted &&
    isLivePlaybackActive(ctx) &&
    job.isLiveStreamProcessing &&
    !playerLiveAdapter.isHlsStreamCompleted;
}

export function getCurrentAbsoluteTimeByDate(ctx: LiveContext): number | null {
  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.currentAbsoluteTimeByDate;
  return ctx.currentTime;
}

export function getCurrentAbsoluteTimeBySN(ctx: LiveContext): number | null {
  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.currentAbsoluteTimeBySN || null;
  return ctx.currentTime;
}

export function getCurrentAbsoluteTime(ctx: LiveContext): number | null {
  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.absoluteCurrentTime || null;
  return ctx.currentTime;
}

export function getReactionTime(ctx: LiveContext): number | null {
  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.liveReactionTime || null;
  return ctx.currentTime;
}
